// STUD-899: everything the landing page states as proof lives here, each item with its source,
// so every claim on the page can be traced back to the product or the owner. Pre-launch rule:
// nothing on the page may be invented — no made-up testimonials, numbers or results.

// A real, verified question from the production bank, copied verbatim (question, options,
// answer and the stored step-by-step explanation). Source: questions._id
// 67731892afa9c8aa0e13b158 (Thinking Skills, difficulty 4), picked read-only on 3 Nov 2026 as
// the cleanest text-only candidate: one valid chain (pictures → children's → large fonts), no
// arguable distractor, explanation accurate as stored. It replaced 680c0d8e… (Quinn), whose
// keyed answer describes the action rather than the reasoning flaw and whose explanation
// genders Quinn. If this question is edited or retired in the bank, update or replace it here.
export const SAMPLE_QUESTION = {
  sourceId: '67731892afa9c8aa0e13b158',
  subject: 'Thinking Skills',
  stimulus: [
    'A library separates children’s books and adult books. The rules:',
    '1) If a book has pictures, it is for children.',
    '2) Any adult book does not contain pictures.',
    '3) All children’s books have large fonts.',
  ],
  prompt: 'Which statement must be true based on these rules?',
  options: {
    A: 'A book with pictures must have large fonts.',
    B: 'Any book for adults has large fonts.',
    C: 'Books with large fonts cannot have pictures.',
    D: 'If a book has pictures, it might be for adults.',
  },
  correctKey: 'A',
  theory: '',
  steps: [
    'From rule (1), if a book has pictures, it is for children.',
    'From rule (3), all children’s books have large fonts.',
    'Combining these means any book with pictures must also have large fonts (because it is automatically a children’s book).',
  ],
};

// Source: NSW Department of Education, checked 2 Nov 2026 —
// https://education.nsw.gov.au/schooling/parents-and-carers/choosing-a-school-setting/selective-high-schools/placement-test
// (test dates) and .../selective-high-schools/application-process (application window).
// Year 7 entry in 2028. Once `testDate` passes, the CTA falls back to undated copy, so a stale
// date never shows; roll this forward for the next cycle.
export const NEXT_SELECTIVE_TEST = {
  label: '13–14 May 2027',
  testDate: '2027-05-13',
  applicationsOpenDate: '2026-11-05',
  applicationsCloseLabel: '15 February 2027',
  applicationsCloseDate: '2027-02-15',
  sourceUrl: 'https://education.nsw.gov.au/schooling/parents-and-carers/choosing-a-school-setting/selective-high-schools/placement-test',
};

// Long-form founder note, verbatim from Founder_Story.md (marketing folder), approved by Pramod
// on 2 Nov 2026. That file is canonical: edit there first, then copy here, so the site and the
// social posts never say different things.
export const FOUNDER_STORY = {
  title: 'Why I built TestCoachAI',
  pullQuote: 'Getting in is a bonus. Getting better is the goal.',
  signature: 'Pramod',
  role: 'Sydney parent and founder',
  // Shown before "Read the full story"; the rest sits behind the toggle.
  leadParagraphs: [
    'I started TestCoachAI while experimenting with how AI could help my own children learn.',
    'Preparing for the selective test, I kept seeing the same pattern. A child meets a new type of problem, learns how to solve it, moves on, and a few weeks later it’s gone. One explanation isn’t enough. What works is meeting the same kind of problem again and again, with different questions on different days, until the method becomes second nature.',
  ],
  moreParagraphs: [
    'So that’s how we practised. One of my children found maths hard at first. We worked through the same question patterns many times over several weeks, and the improvement from where they started was significant.',
    'I looked for a product built around that idea and couldn’t find one. Most give you more questions and move on. I wanted something that deliberately brings back what a child got wrong until it’s secure, and shows parents clearly what’s improving. That’s TestCoachAI.',
    'There’s one more thing I believe as a parent. Selective places are limited, and plenty of children who work hard and improve a great deal won’t get one. That isn’t failure. By all means go for it. But measure success by how far your child has come compared with a year ago, not only by the offer letter. The discipline of steady practice, and the experience of preparing for a competitive exam early, stay with a child long after test day.',
    'TestCoachAI makes that process easy to follow and easy to see. Getting in is a bonus. Getting better is the goal.',
    'I’m still building it, and I’ll keep improving it with the families who use it.',
  ],
};

// Screenshots captured from the running app (local) with the E2E test accounts, so no real family's
// data. Every question shown in them is a verified question in the PRODUCTION bank (owner's rule,
// 3 Nov 2026: local questions may be unreviewed, so never screenshot one that isn't in prod).
// Matching on question text is NOT enough: local holds several same-stem variants with different
// options (some with wrong keys). So the prod documents were copied into local under their prod
// _ids and the practice pools were filtered to those exact ids:
//  - practice: "Runners' Race Gap", prod questions._id 67df44dabf60eb2464a75365, answered with the
//    tempting 80 m (10 s × 8 m/s). Chosen over the median question (correct, but its solution
//    guesses x = 6 rather than showing why) and the square-stage question (the player splits its
//    "Theory —" / "Steps:" / "Why wrong:" headings into 14 separate steps). Captured at tablet
//    width so the image is not a tall phone strip beside a short caption.
//  - Practice Mistakes and Select & Ask: "Top View Puzzle Plan 2", prod 6a119c3ed372d25b64f49987
//    (key Plan E, checked against its diagram scene). The test student answered it wrong in Daily
//    Practice, then it came back in Practice Mistakes. The Ask answer is a live AI explanation
//    (Select & Ask, STUD-563), as a student would get it.
//  - writing: prompt "Social media should be banned for students under 16. Argue your case.",
//    identical text to verified prod prompt 6a19707a317226999a94e38d. Both feedback paragraphs
//    are AI output from utils/writingMarker.js; since STUD-925 the report labels them as AI and
//    carries the AI note, and the caption says so too.
// Each image is cropped at a clean edge in the file itself, so the page shows it whole (never
// CSS-cropped). Re-capture when these screens change visibly, and re-check the prod ids above.
export const PRODUCT_FEATURES = [
  {
    title: 'Every answer is explained',
    caption: 'Instant feedback in Daily Practice: the right answer, then a worked solution your child can open step by step.',
    images: [{
      src: '/landing/practice-explanation.jpg', width: 900, height: 1173,
      alt: 'Daily Practice: a race question with the chosen answer, 80 m, marked wrong, the correct answer, 60 m, marked right, and the three-step worked solution.',
    }],
  },
  {
    title: 'Mistakes come back until they stick',
    caption: 'Practice Mistakes brings back the questions your child got wrong, marked “Previously incorrect”, most recent and most repeated first.',
    images: [{
      src: '/landing/practice-mistakes.jpg', width: 900, height: 1159,
      alt: 'A Practice Mistakes session: a top-view puzzle question, tagged Previously incorrect, today, with an angled view of the piece and five labelled plans.',
    }],
  },
  {
    title: 'Ask about any word or step',
    caption: 'Stuck on a phrase? Your child drags across it — in the question, an answer or the working — and asks “What does this mean?” or “Explain this simply”. Answers are written by AI and can contain mistakes.',
    images: [
      {
        src: '/landing/ask-menu.jpg', width: 900, height: 247,
        alt: 'The words "should not show empty gaps" selected in a question, with a menu offering What does this mean? and Explain this simply.',
      },
      {
        src: '/landing/ask-answer.jpg', width: 900, height: 1013,
        alt: 'An AI explanation of "should not show empty gaps": the top view marks only squares with a cube and leaves gaps blank.',
      },
    ],
  },
  {
    title: 'Writing marked out of 25',
    caption: 'Each piece gets a band and a score out of 25, then two kinds of feedback: an examiner-style verdict on the piece, and tutor-style coaching on what to do next. Marking and feedback are done by AI, not a person.',
    images: [{
      src: '/landing/writing-feedback.jpg', width: 900, height: 604,
      alt: 'An AI writing report: Exceptional, 23 out of 25, an AI-generated feedback note, then Examiner-style feedback (AI) and How to improve (AI).',
    }],
  },
  {
    title: 'Progress you can check anytime',
    caption: 'The parent dashboard charts weekly scores and the four-week trend.',
    images: [{
      src: '/landing/parent-dashboard-progress.jpg', width: 640, height: 496,
      alt: 'Parent dashboard Score Over Time chart showing weekly average percentage and a four-week trend line.',
    }],
  },
];

// Quotes from soft-launch families. Add one ONLY with the family's permission recorded in
// Outreach_Log.md (quote permission = Y, and how they want to be attributed). The section stays
// hidden while this is empty.
export const FAMILY_QUOTES = [];
